import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const NAV_WIDTH_TRANSITION_DURATION_MS = 400;

type NavigationMenuContextValue = {
    readonly closeMenu: () => void;
    readonly isExpanded: boolean;
    readonly isHovered: boolean;
    readonly isOpen: boolean;
    readonly openMenu: () => void;
    readonly setHovered: (hovered: boolean) => void;
    readonly shouldContentOverflow: boolean;
    readonly toggleMenu: () => void;
};

const NavigationMenuContext = createContext<NavigationMenuContextValue | null>(null);

const STORAGE_KEY = 'navigation-menu-open';

const getStoredMenuState = (): boolean => {
    try {
        const stored = globalThis.localStorage.getItem(STORAGE_KEY);

        if (stored === 'true') {
            return true;
        }

        return false;
    } catch {
        return false;
    }
};

const saveMenuState = (isOpen: boolean): void => {
    try {
        globalThis.localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
        // localStorage not available, silently ignore
    }
};

const useOverflowLock = ({ isHovered, isOpen }: { readonly isHovered: boolean; readonly isOpen: boolean }): boolean => {
    const [isOverflowLockActive, setIsOverflowLockActive] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            setIsOverflowLockActive(false);
            return;
        }

        if (isHovered) {
            setIsOverflowLockActive(true);
        }
    }, [isHovered, isOpen]);

    useEffect(() => {
        if (isOpen || isHovered || !isOverflowLockActive) {
            return;
        }

        const timeoutId = globalThis.setTimeout(() => {
            setIsOverflowLockActive(false);
        }, NAV_WIDTH_TRANSITION_DURATION_MS);

        return () => {
            globalThis.clearTimeout(timeoutId);
        };
    }, [isOverflowLockActive, isHovered, isOpen]);

    return isOverflowLockActive;
};

export const NavigationMenuProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [isOpen, setIsOpen] = useState<boolean>(getStoredMenuState);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const isOverflowLockActive = useOverflowLock({ isHovered, isOpen });

    const closeMenu = useCallback((): void => {
        setIsOpen(false);
        saveMenuState(false);
    }, []);

    const openMenu = useCallback((): void => {
        setIsOpen(true);
        saveMenuState(true);
    }, []);

    const toggleMenu = useCallback((): void => {
        setIsOpen((prev) => {
            const newState = !prev;
            saveMenuState(newState);

            return newState;
        });
    }, []);

    const setHovered = useCallback((hovered: boolean): void => {
        setIsHovered(hovered);
    }, []);

    const isExpanded = isOpen || isHovered;
    const shouldContentOverflow = !isOpen && (isHovered || isOverflowLockActive);

    return (
        <NavigationMenuContext.Provider
            value={{
                closeMenu,
                isExpanded,
                isHovered,
                isOpen,
                openMenu,
                setHovered,
                shouldContentOverflow,
                toggleMenu,
            }}
        >
            {children}
        </NavigationMenuContext.Provider>
    );
};

export const useNavigationMenu = (): NavigationMenuContextValue => {
    const context = useContext(NavigationMenuContext);

    if (context === null) {
        throw new Error('useNavigationMenu must be used within NavigationMenuProvider');
    }

    return context;
};
